import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Container, Row, Col, Form, Button } from "react-bootstrap";
import $ from "jquery";
import TeamMemberService from "../../services/TeamMemberService";
import UserSessionManager from "../../utils/UserSessionManager";
import { getAccessToken } from "../../utils/authStorage";
import { getSubdomainFromUrl } from '../../utils/tenant';        
import { setSubdomain } from '../../utils/authStorage'; 

const ChangePassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // Read subdomain from URL automatically
  const urlSubdomain = getSubdomainFromUrl();
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
  const defaultAppId = process.env.REACT_APP_DEFAULT_APP_ID;

  const sessionEmail = UserSessionManager.getUserInfo()?.email || UserSessionManager.getSession()?.email || "";
  const email = location.state?.email || sessionEmail;
  const forced = UserSessionManager.requiresPasswordChange() && Boolean(getAccessToken());

  const [currentPassword, setCurrentPassword] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleForgotPasswordChange = () => {
    const subdomain = urlSubdomain || null; 
    const requestData = {
      email,
      password,
      accountId: defaultAppId,
      subdomain,
    };

    $.ajax({
      url: `${apiBaseUrl}/Auth/change-password`,
      type: "PATCH",
      contentType: "application/json",
      data: JSON.stringify(requestData),
      success: function (response) {
        if (response.success && response.data) {
          setErrorMessage("");
          setSuccessMessage("Password changed successfully! Redirecting...");
          setTimeout(() => navigate("/signin"), 2000);
        } else {
          setSuccessMessage("");
          setErrorMessage(response.message || "Password change failed.");
        }
        setSubmitting(false);
      },
      error: function () {
        setSuccessMessage("");
        setErrorMessage("Something went wrong!");
        setSubmitting(false);
      },
    });
  };

  const handleForcedPasswordChange = async () => {
    if (!email.trim()) {
      setErrorMessage("Email is missing. Please sign in again.");
      setSubmitting(false);
      return;
    }

    try {
      const response = await TeamMemberService.changeTeamMemberPassword({
        email: email.trim(),
        password,
        accountId: defaultAppId,
        currentPassword,
      });

      if (response.data?.success) {
        UserSessionManager.updateSession({ isDefaultPassword: false });
        setErrorMessage("");
        setSuccessMessage("Password changed successfully! Redirecting...");
        setTimeout(() => navigate("/dashboard"), 1500);
      } else {
        setSuccessMessage("");
        setErrorMessage(response.data?.message || "Password change failed.");
      }
    } catch (error) {
      setSuccessMessage("");
      setErrorMessage(
        error.response?.data?.message || "Something went wrong. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();

    if (forced && !currentPassword.trim()) {
      setErrorMessage("Current password is required.");
      setSuccessMessage("");
      return;
    }

    if (password !== confirmPassword) {
      setErrorMessage("Passwords do not match.");
      setSuccessMessage("");
      return;
    }

    setSubmitting(true);
    setErrorMessage("");
    setSuccessMessage("");

    if (forced) {
      await handleForcedPasswordChange();
      return;
    }

    handleForgotPasswordChange();
  };

  return (
    <div className="bg-light py-5">
      <Container className="bg-white rounded shadow p-4">
        <Row className="align-items-center">
          <Col md={6} className="text-center">
            <img
              src="/assets/images/login-illustration.png"
              alt="Change Password Illustration"
              className="img-fluid rounded mb-3"
              style={{ maxHeight: "300px" }}
            />
            <p className="text-muted fs-5">
              {forced
                ? "Set a new password to access your account"
                : "Secure your account with a strong password"}
            </p>
          </Col>

          <Col md={6}>
            <div className="p-3">
              <h2 className="fw-bold mb-2 d-flex align-items-center gap-2">
                Change Password
                <img src="/assets/images/logo.png" alt="Logo" width="50" />
              </h2>

              {forced && (
                <p className="text-muted mb-3">
                  You are using a temporary password. Please change it before
                  continuing to the application.
                </p>
              )}

              <Form onSubmit={handleChangePassword}>
                {errorMessage && (
                  <div className="text-danger mb-3 text-center fw-semibold">
                    {errorMessage}
                  </div>
                )}
                {successMessage && (
                  <div className="text-success mb-3 text-center fw-semibold">
                    {successMessage}
                  </div>
                )}

                {forced && (
                  <Form.Group className="mb-3" controlId="formCurrentPassword">
                    <Form.Label>Current Password</Form.Label>
                    <Form.Control
                      type="password"
                      placeholder="Enter your temporary password"
                      required
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      disabled={submitting}
                    />
                  </Form.Group>
                )}

                <Form.Group className="mb-3" controlId="formPassword">
                  <Form.Label>New Password</Form.Label>
                  <Form.Control
                    type="password"
                    placeholder="Enter new password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={submitting}
                  />
                </Form.Group>

                <Form.Group className="mb-3" controlId="formConfirmPassword">
                  <Form.Label>Confirm Password</Form.Label>
                  <Form.Control
                    type="password"
                    placeholder="Confirm new password"
                    required
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    disabled={submitting}
                  />
                </Form.Group>

                <Button
                  type="submit"
                  className="w-100 text-white fw-semibold"
                  style={{
                    backgroundColor: "#CC5500",
                    border: "none",
                  }}
                  disabled={submitting}
                >
                  {submitting ? "Changing..." : "Change Password"}
                </Button>
              </Form>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default ChangePassword;
